import { swSettings } from "app/common/services/swSettings";
import {
    MAX_ALLOWED_DURATION_BEFORE_LIMIT_SEGMENTS,
    MAX_ALLOWED_DURATION_AFTER_LIMIT_SEGMENTS,
    WW_MAX_ALLOWED_DURATION_BEFORE_LIMIT_SEGMENTS,
    WW_MAX_ALLOWED_DURATION_AFTER_LIMIT_SEGMENTS,
} from "app/components/widget/widget-config/metrics/segments/utils";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import type {
    ICustomSegmentAccount,
    ICustomSegmentGroupWebsite,
    ICustomSegmentsGroup,
    UniqueSegments,
} from "app/services/segments/segmentsApiService";

import type { AvaliableDurations } from "../../utils/dashboardWizardDefaultState";

import { MAX_SEGMENTS_ALLOWED_DURATION_FILTER, MAX_SEGMENTS_COUNT, WEBSITE_ID_KEY } from "./constants";
import type { SegmentsFromGroup, SelectedSegmentsId, SingleSegmentId } from "./types";

export const getFirstSevenSegments = (segments: SelectedSegmentsId) => {
    const result = {};

    Object.keys(segments)
        .sort()
        .slice(0, MAX_SEGMENTS_COUNT)
        .forEach((segmentId: string) => {
            result[segmentId] = true;
        });

    return result;
};

export const getArrayOfSegments = (
    segments: SelectedSegmentsId | string[],
    segmentsAndWebsitesByGroup: ICustomSegmentAccount[],
): ICustomSegmentAccount[] => {
    const selectedSegments = Array.isArray(segments) ? segments : Object.keys(segments);

    return segmentsAndWebsitesByGroup.filter((segment) => selectedSegments.includes(segment.id));
};

export const getArrayOfSegmentsId = (segments: SelectedSegmentsId) => Object.keys(segments);

export const getSegmentsIdsFromGroup = (groupId: string, segmentsGroups: ICustomSegmentsGroup[]): SegmentsFromGroup => {
    if (segmentsGroups) {
        const segmentsIds = {
            size: 0,
            ids: {},
            hasWebsites: false,
        };

        const selectedGroup = segmentsGroups.find((segemntGroup) => segemntGroup.id === groupId);

        if (selectedGroup?.segments) {
            selectedGroup.segments.forEach((id: string) => {
                segmentsIds.ids[id] = 1;
                segmentsIds.size++;
            });

            if (selectedGroup?.websites) {
                selectedGroup.websites.forEach((website: ICustomSegmentGroupWebsite) => {
                    segmentsIds.ids[`WEBSITE#${website.domain}`] = WEBSITE_ID_KEY;
                    segmentsIds.size++;
                });

                segmentsIds.hasWebsites = true;
            }
        }

        return segmentsIds;
    }
};

export const mergeSegmentIds = (
    uniqueSegmentIds?: UniqueSegments,
    selectedSegmentIds?: SelectedSegmentsId | SingleSegmentId,
) => {
    if (selectedSegmentIds && uniqueSegmentIds) {
        const idsArray = Array.isArray(selectedSegmentIds) ? selectedSegmentIds : Object.keys(selectedSegmentIds);

        const result = {
            ...uniqueSegmentIds,
        };

        idsArray.forEach((id) => {
            if (result[id] >= 1) {
                result[id] = result[id] + 1;
            } else if (!result[id] && !id.includes(WEBSITE_ID_KEY)) {
                result[id] = 1;
            }
        });

        return result;
    }
};

export const removeInitialSegmentsIds = (
    uniqueSegmentIds: UniqueSegments,
    selectedSegmetIds: SelectedSegmentsId | SingleSegmentId,
) => {
    if (uniqueSegmentIds && selectedSegmetIds) {
        const selectedSegmetIdsArray = Array.isArray(selectedSegmetIds)
            ? selectedSegmetIds
            : Object.keys(selectedSegmetIds);
        const uniqueSegmentIdsMap = new Map(Object.entries(uniqueSegmentIds));

        selectedSegmetIdsArray.forEach((id: string) => {
            const currentValue = uniqueSegmentIdsMap.get(id);

            if (currentValue === 1) {
                uniqueSegmentIdsMap.delete(id);
            } else if (currentValue > 1) {
                uniqueSegmentIdsMap.set(id, currentValue - 1);
            }
        });

        return Object.fromEntries(uniqueSegmentIdsMap);
    }
};

export const getSegmentsAndWebsitesByGroup = (
    accountSegments: ICustomSegmentAccount[],
    segmentsGroups: ICustomSegmentsGroup[],
    selectedGroupId: string,
    segmentsIdsFromGroup: SegmentsFromGroup,
    hasWebsites: boolean,
) => {
    const segmentsAndWebsites = [];

    const segmentsMap = accountSegments.reduce(
        (acc, segment) => {
            acc[segment.id] = segment;
            return acc;
        },
        {} as Record<string, ICustomSegmentAccount>,
    );

    const groupSegmentIds = Object.keys(segmentsIdsFromGroup?.ids ?? {});

    for (const id of groupSegmentIds) {
        if (segmentsMap[id]) {
            const maxSize = segmentsIdsFromGroup?.size ?? 0;
            if (maxSize === 0) {
                continue;
            }
            if (segmentsAndWebsites.length < maxSize) {
                segmentsAndWebsites.push(segmentsMap[id]);
            }
        }
    }

    if (hasWebsites) {
        const foundGroup = segmentsGroups.find((g) => g.id === selectedGroupId);
        if (foundGroup?.websites) {
            segmentsAndWebsites.push(...foundGroup.websites);
        }
    }

    return segmentsAndWebsites;
};

export const getDurationBySegmentsCount = (
    selectedSegmentGroupId: string,
    segmentGroups: ICustomSegmentsGroup[],
    selectedCountry: number,
) => {
    const selectedGroup = segmentGroups.find((group) => group.id === selectedSegmentGroupId);
    const isWW = selectedCountry === WORLDWIDE_COUNTRY_ID;

    if (selectedGroup.segments.length > MAX_SEGMENTS_ALLOWED_DURATION_FILTER) {
        return isWW ? WW_MAX_ALLOWED_DURATION_AFTER_LIMIT_SEGMENTS : MAX_ALLOWED_DURATION_AFTER_LIMIT_SEGMENTS;
    }

    return isWW ? WW_MAX_ALLOWED_DURATION_BEFORE_LIMIT_SEGMENTS : MAX_ALLOWED_DURATION_BEFORE_LIMIT_SEGMENTS;
};

export const getSegmentsAvaliableDuration = (durationPreset: string[], avaliableDuration: AvaliableDurations[]) =>
    avaliableDuration.map((duration) => {
        if (durationPreset.includes(duration.value)) {
            return {
                ...duration,
                enabled: false,
                locked: true,
            };
        } else {
            return {
                ...duration,
                enabled: true,
                locked: false,
            };
        }
    });

export const removeSegmentsFromSelected = (segmentsIds: string[], segments: Record<string, number>) => {
    const updatedUniqueSegmentsMap = new Map(Object.entries(segments));

    segmentsIds.forEach((id: string) => {
        const currentValue = updatedUniqueSegmentsMap.get(id);

        if (currentValue === 1) {
            updatedUniqueSegmentsMap.delete(id);
        } else {
            updatedUniqueSegmentsMap.set(id, currentValue - 1);
        }
    });

    return Object.fromEntries(updatedUniqueSegmentsMap);
};

export const getIsDisabledWithSegments = (segmentsIds: Record<string, number>) => {
    const { SegmentsPerDashboardHardLimit } = swSettings?.current?.resources;

    if (!segmentsIds) {
        return false;
    }

    const usedSegmentsNum = Object.keys(segmentsIds);

    if (usedSegmentsNum.length <= SegmentsPerDashboardHardLimit) {
        return false;
    }

    return true;
};
